import { useState, type ReactNode } from "react";
import { Bookmark, Image, MessageSquare, Save } from "lucide-react";
import { AgenticFeedbackControls } from "../shared/AgenticFeedbackControls";
import { CatalystImage } from "../shared/CatalystImage";
import { FadingRuler } from "../shared/FadingRuler";
import {
  buildRiskSignalInterpretation,
  formatAge,
  imageUnavailableCopy,
  type RiskSignal,
} from "./risk-signal-card-utils";
import {
  askHarperAboutRiskSignal,
  queueRiskSignalBookmark,
  saveRiskSignalArtifact,
  shareRiskSignalImageCard,
} from "./risk-signal-actions";
import type { RiskSignalDrift } from "./useRiskSignalDrift";

interface RiskSignalExpandedDetailsProps {
  signal: RiskSignal;
  displayedAnalysis: string;
  isPendingRefinement: boolean;
  drift?: RiskSignalDrift;
  microSize: string;
  metaSize: string;
  textSize: string;
}

type ActionState = "idle" | "saved" | "shared";

export function RiskSignalExpandedDetails({
  signal,
  displayedAnalysis,
  isPendingRefinement,
  drift,
  microSize,
  metaSize,
  textSize,
}: RiskSignalExpandedDetailsProps) {
  const [actionState, setActionState] = useState<ActionState>("idle");
  const read = buildRiskSignalInterpretation(signal);
  const unavailableCopy = imageUnavailableCopy(signal);

  const mark = (state: ActionState) => {
    setActionState(state);
    window.setTimeout(() => setActionState("idle"), 1600);
  };

  return (
    <div className="relative ml-5 mt-2 space-y-2 px-2 py-2 pr-16 max-[767px]:ml-4 max-[767px]:pr-3">
      <FadingRuler />
      <div className="grid gap-2 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <div className="min-w-0">
          {signal.imageUrl ? (
            <CatalystImage
              imageUrl={signal.imageUrl}
              href={signal.imageSourceUrl ?? signal.imageUrl}
              alt={signal.title}
              maxHeight={150}
              className="rounded-md"
            />
          ) : (
            <div
              className={`${microSize} rounded-md border border-[var(--fintheon-accent)]/12 bg-[var(--fintheon-bg)]/45 px-2 py-2 uppercase tracking-[0.14em] text-[var(--fintheon-muted)]/36`}
            >
              {unavailableCopy}
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-1.5">
          <p
            className={`${textSize} text-[var(--fintheon-text)]/70 leading-relaxed max-[767px]:text-[var(--fintheon-text)]/78`}
          >
            {displayedAnalysis}
          </p>
          {isPendingRefinement && (
            <div
              className={`${microSize} uppercase tracking-[0.16em] text-[var(--fintheon-accent)]/55`}
            >
              AI refinement pending
            </div>
          )}
          <div className="flex items-center gap-2">
            <span
              className={`${microSize} text-[var(--fintheon-muted)]/30 uppercase tracking-wider`}
            >
              Estimated Drift
            </span>
            {drift?.loading ? (
              <span className="h-3 w-20 rounded-sm bg-[var(--fintheon-accent)]/10 animate-pulse" />
            ) : (
              <span
                className={`${metaSize} text-[var(--fintheon-accent)] font-mono font-medium`}
              >
                {drift?.label ?? "-"}
              </span>
            )}
          </div>
        </div>
      </div>

      <InterpretationGrid
        read={read}
        microSize={microSize}
        textSize={textSize}
      />

      <SignalDiagnostics
        signal={signal}
        microSize={microSize}
        textSize={textSize}
      />

      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        <ActionButton
          label="Ask"
          icon={<MessageSquare className="h-3 w-3" />}
          onClick={() => askHarperAboutRiskSignal(signal)}
        />
        <ActionButton
          label="Bookmark"
          icon={<Bookmark className="h-3 w-3" />}
          onClick={() => {
            queueRiskSignalBookmark(signal);
            mark("saved");
          }}
        />
        <ActionButton
          label="Save"
          icon={<Save className="h-3 w-3" />}
          onClick={() => {
            saveRiskSignalArtifact(signal);
            mark("saved");
          }}
        />
        <ActionButton
          label="Image"
          icon={<Image className="h-3 w-3" />}
          onClick={() => {
            void shareRiskSignalImageCard(signal).finally(() => mark("shared"));
          }}
        />
        {actionState !== "idle" && (
          <span
            className={`${microSize} uppercase tracking-wider text-[var(--fintheon-accent)]/60`}
          >
            {actionState}
          </span>
        )}
      </div>

      <AgenticFeedbackControls surface="risk-signals" itemId={signal.id} />
    </div>
  );
}

function InterpretationGrid({
  read,
  microSize,
  textSize,
}: {
  read: ReturnType<typeof buildRiskSignalInterpretation>;
  microSize: string;
  textSize: string;
}) {
  const rows = [
    ["Changed", read.whatChanged],
    ["Matters", read.whyItMattersNow],
    ["Wind", read.headwindTailwindRead],
    ["Blind Spot", read.whoLikelyDoesNotKnow],
    ["Priced", read.pricedInRead],
    ["Desk", read.deskImplication],
    ["Invalidates", read.invalidatesIf],
  ] as const;
  return (
    <div className="grid gap-1.5 md:grid-cols-2">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="min-w-0 rounded-md bg-white/[0.025] px-2 py-1.5"
        >
          <div
            className={`${microSize} uppercase tracking-[0.14em] text-[var(--fintheon-muted)]/30`}
          >
            {label}
          </div>
          <div
            className={`${textSize} mt-0.5 leading-relaxed text-[var(--fintheon-text)]/62`}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

function SignalDiagnostics({
  signal,
  microSize,
  textSize,
}: {
  signal: RiskSignal;
  microSize: string;
  textSize: string;
}) {
  const lines = [
    `Generated ${formatAge(signal.generatedAt)}`,
    signal.imageGeneratedAt
      ? `image ${formatAge(signal.imageGeneratedAt)}`
      : null,
    signal.degradationReason ?? signal.freshnessDiagnostics ?? null,
  ].filter((line): line is string => Boolean(line));

  return (
    <div className="space-y-1">
      <div
        className={`${microSize} uppercase tracking-wider text-[var(--fintheon-muted)]/30`}
      >
        Freshness / Diagnostics
      </div>
      <div className={`${textSize} text-[var(--fintheon-muted)]/50`}>
        {lines.join(" / ")}
      </div>
    </div>
  );
}

function ActionButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-[25px] items-center gap-1 rounded border border-[var(--fintheon-accent)]/14 px-2 text-[9px] uppercase tracking-wider text-[var(--fintheon-accent)]/62 transition-colors hover:border-[var(--fintheon-accent)]/34 hover:bg-[var(--fintheon-accent)]/10 hover:text-[var(--fintheon-accent)]"
    >
      {icon}
      {label}
    </button>
  );
}
